import React, { useState, useEffect, useRef } from "react";
import LabelAndToggle from "./LabelAndToggle";
import { CheckAnswer, CogIcon, TrashIcon } from "../../../../../assets/icons";
import { v4 as uuidv4 } from 'uuid';
import { useDrag, useDrop } from 'react-dnd';

const ItemTypes = {
  ELEMENT: 'element',
};

const areQuestionsEqual = (first, second) => {
  if (!Array.isArray(first) || !Array.isArray(second)) return false;
  return JSON.stringify(first) === JSON.stringify(second);
};

const areOptionsEqual = (first, second) => {
  if (!Array.isArray(first) || !Array.isArray(second)) return false;
  return JSON.stringify(first) === JSON.stringify(second);
};

// Answer Item Component
function AnswerItem({ answer, index, handleAnswerChange, toggleCheckAnswer, deleteAnswer, moveAnswer, visibility }) {
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.ELEMENT,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: ItemTypes.ELEMENT,
    drop: (item) => {
      moveAnswer(item.index, index);
    },
  });

  return (
    <div key={index} className="item-list-flex-design" ref={(node) => drag(drop(node))}>
      <div className={`flex-third-col-design ${answer.isChecked ? "active" : ""}`}>
        <div className="icon-flex-third-col-design">
          <CogIcon />
        </div>
        <div className="filed-design-form flex-full">
          <input
            type="text"
            className="input-design-form"
            placeholder="Write here..."
            value={answer.value}
            onChange={(e) => handleAnswerChange(index, e.target.value)}
          />
        </div>
        {visibility && (
          <div onClick={() => toggleCheckAnswer(index)} className="question-correct-answer">
            <CheckAnswer />
          </div>
        )}
        <div onClick={() => deleteAnswer(index)} className="button-delete"><TrashIcon /></div>
      </div>
    </div>
  );
}

function QuestionItem({ question, index, handleQuestionChange, deleteQuestion, visibility }) {
  const options = question.options || [];

  const updateOptions = (updater) => {
    const nextOptions = typeof updater === 'function' ? updater(options) : updater;
    if (areOptionsEqual(options, nextOptions)) return;
    handleQuestionChange(index, { ...question, options: nextOptions });
  };

  const handleAnswerChange = (answerIndex, newValue) => {
    updateOptions((prev) =>
      prev.map((answer, i) => (i === answerIndex ? { ...answer, value: newValue } : answer))
    );
  };

  const toggleCheckAnswer = (answerIndex) => {
    updateOptions((prev) =>
      prev.map((option, i) => answerIndex === i ? {...option, isChecked: !option.isChecked } : {...option, isChecked: false})
    );
  };

  const deleteAnswer = (answerIndex) => {
    updateOptions((prev) => prev.filter((_, i) => i !== answerIndex));
  };

  const addNewOption = () => {
    updateOptions((prev) => [
      ...prev,
      { id: uuidv4(), value: "", isChecked: false },
    ]);
  };

  const moveAnswer = (dragIndex, hoverIndex) => {
    updateOptions((prev) => {
      const dragAnswer = prev[dragIndex];
      if (!dragAnswer) return prev;
      const newoptions = [...prev];
      newoptions.splice(dragIndex, 1);
      newoptions.splice(hoverIndex, 0, dragAnswer);
      return newoptions;
    });
  };

  const setAnswer = (e) => {
    updateOptions((prev) =>
      prev.map((option, i) => i === 0 ? {...option, isChecked: e } : {...option, isChecked: false})
    );
  }

  return (
    <div className="question-item">
      <div className="question-header">
        <label>{`Question\u00A0${index + 1}`}</label>
        <input
          type="text"
          className="input-design-form"
          placeholder="Question text..."
          value={question.question}
          onChange={(e) =>
            handleQuestionChange(index, { ...question, question: e.target.value })
          }
        />
        <div className="button-delete" onClick={() => deleteQuestion(index)}>
          <TrashIcon />
        </div>
      </div>

      <div className="list-flex-design list-question-design">
        {visibility && <LabelAndToggle title="Set correct answer" checked={options.filter(ele => ele.isChecked).length > 0} setChecked={setAnswer}/>}
        {options.map((answer, idx) => (
          <AnswerItem
            key={answer.id || idx}
            answer={answer}
            index={idx}
            handleAnswerChange={handleAnswerChange}
            toggleCheckAnswer={toggleCheckAnswer}
            deleteAnswer={deleteAnswer}
            moveAnswer={moveAnswer}
            visibility={visibility}
          />
        ))}
      </div>
      <div className="cover-add-btn-design">
        <button onClick={addNewOption} type="button" className="button-add btn-add-question add-option" >
          Add option
        </button>
      </div>
      <hr className="mb-2"/>
    </div>
  )
}

const MultipleChoiceQuestionAnswer = ({ defaultValue, onChange, visibility = false }) => {
  const onChangeRef = useRef(onChange);
  const fallbackQuestions = [
    {
      id: uuidv4(),
      question: "Enter question",
      options: [
        { id: uuidv4(), value: "Option 1", isChecked: false },
        { id: uuidv4(), value: "Option 2", isChecked: false },
      ],
    },
  ];

  const [questions, setQuestions] = useState(
    Array.isArray(defaultValue) && defaultValue.length > 0 ? defaultValue : fallbackQuestions
  );

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (Array.isArray(defaultValue) && defaultValue.length > 0) {
      setQuestions((prev) => (areQuestionsEqual(prev, defaultValue) ? prev : defaultValue));
    }
  }, [defaultValue]);

  const commitQuestions = (updater) => {
    setQuestions((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (areQuestionsEqual(prev, next)) return prev;
      onChangeRef.current?.(next);
      return next;
    });
  };

  const handleQuestionChange = (index, updatedQuestion) => {
    commitQuestions((prev) =>
      prev.map((question, i) => (i === index ? updatedQuestion : question))
    );
  };

  const addNewQuestion = () => {
    commitQuestions((prev) => [
      ...prev,
      { id: uuidv4(), question: "", options: [] },
    ]);
  };

  const deleteQuestion = (index) => {
    commitQuestions((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="questions-list">
      {questions.map((question, idx) => (
        <QuestionItem
          key={question.id}
          question={question}
          index={idx}
          handleQuestionChange={handleQuestionChange}
          deleteQuestion={deleteQuestion}
          visibility={visibility}
        />
      ))}
      <div className="cover-add-btn-design">
        <button
          onClick={addNewQuestion}
          type="button"
          style={{width: "100%"}}
          className="button-add btn-add-question d-flex"
        >
          Add Question
        </button>
      </div>
    </div>
  );
}

export default MultipleChoiceQuestionAnswer;
